import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { stripePromise } from "../config/stripe";
import { LocationProvider, useAuth } from "@healthdesk/shared-hooks";
// Auth pages
import PhoneAuthPage from "../pages/auth/PhoneAuthPage";
import QrLoginClaimPage from "../pages/auth/QrLoginClaimPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import WPAuthHandler from "../pages/auth/WPAuthHandler";
// Dashboard pages
import KijabeDashboard from "../pages/pages/dashboard/KijabeDashboard";
// Chat pages
import KijabeChatPage from "../pages/pages/KijabeChatPage";
import ChatPageWrapper from "../pages/pages/ChatPageWrapper";
import AskMeAnythingPage from "../components/chatbot/AskMeAnythingPage";
import AskMeAnything2Page from "../components/chatbot/AskMeAnything2Page";
import QueryChatPage from "../components/chatbot/QueryChatPage";
// Virtual Queue pages
import PatientRegistration from "../components/vaccine/queue/PatientRegistration";
// Legal pages
import PrivacyPolicy from "../components/auth/documents/PrivacyPolicy";
import TermsOfUse from "../components/auth/documents/TermsOfUse";
import Baa from "../components/auth/documents/baa";
// Account pages
import Verify from "../components/dashboard/Verify";
import Onboarding from "../components/auth/Onboarding";
// ChartMind pages
import { ChartmindAdminDashboard } from '../features/chartmind/components/chartmind/admin';
// Unified join (single entry per plan: /join/:token)
import UnifiedJoinPage from '../pages/join/UnifiedJoinPage';
import InvitationAcceptPage from '../pages/auth/InvitationAcceptPage';
// Splash (first page load at root)
import MSI3SplashPage from "../pages/splash/MSI3SplashPage";
import MSISplashAltRoute from "../pages/splash/MSISplashAltRoute";
import SplitFlapDisplayPage from "../pages/splash/SplitFlapDisplayPage";
import TitleLogosPage from "../pages/splash/TitleLogosPage";
import MSI3OnlyPage from "../pages/splash/MSI3OnlyPage";
import MSI3StandalonePage from "../pages/dashboard/MSI3StandalonePage";
// Test pages (dev/experimental only)
import QRScannerTestPage from "../pages/pages/test/QRScannerTestPage";
// Check-in pages
import PatientCheckInPage from "../pages/checkin/PatientCheckInPage";
import EdClaimEmailVerifyPage from "../pages/claim/EdClaimEmailVerifyPage";
// Cold-provider CMR share (URL from patient QR / camera)
import ProviderCmrSharePage from "../pages/provider/ProviderCmrSharePage";
// Standalone facility sub-routes (parent layout is lazy-loaded below)
import UnifiedQueueDashboard from "../features/facility/components/queue/UnifiedQueueDashboard";
import CheckInFormBuilder from "../features/chartmind/components/supervision/CheckInFormBuilder";
import { Settings } from "@healthdesk/shared-components";
import StandaloneAdvertisingPage from "../pages/getonthemap/StandaloneAdvertisingPage";
import ClinicHealthPage from "../features/facility/pages/ClinicHealthPage";
import { StandaloneConversationPage } from "../features/conversationDispatch";

const MapPage = lazy(() => import("../pages/pages/map/MapPage"));
const SCMapPage = lazy(() => import("../pages/pages/scmap/SCMapPage"));
const ScmapHelpPage = lazy(() => import("../pages/pages/scmap/ScmapHelpPage"));
const Scmap2Page = lazy(() => import("../pages/pages/scmap2/Scmap2Page"));
const Scmap2HelpPage = lazy(() => import("../pages/pages/scmap2/Scmap2HelpPage"));
const DashboardPage = lazy(() => import("../pages/DashboardPage"));
const ChartMindStandaloneLayout = lazy(() =>
  import("../pages/chartmind/ChartMindStandaloneLayout")
);
const ChartMindV2StandaloneLayout = lazy(() =>
  import("../pages/chartmind-v2/ChartMindV2StandaloneLayout")
);
const SymptomCheckerStandaloneLayout = lazy(() =>
  import("../pages/symptomchecker/SymptomCheckerStandaloneLayout")
);
const SymptomCheckV2StandaloneLayout = lazy(() =>
  import("../features/patient/components/symptomCheckV2/SymptomCheckV2StandaloneLayout")
);
const SymptomCheckV3StandaloneLayout = lazy(() =>
  import("../features/patient/components/symptomCheckV3/SymptomCheckV3StandaloneLayout")
);
const SymptomCheckV4StandaloneLayout = lazy(() =>
  import("../features/patient/components/symptomCheckV4/SymptomCheckV4StandaloneLayout")
);
const FacilityStandaloneLayout = lazy(() =>
  import("../pages/getonthemap/FacilityStandaloneLayout")
);
const StandaloneRecordingView = lazy(() =>
  import("../pages/chartmind/ChartMindStandaloneViews").then((m) => ({
    default: m.StandaloneRecordingView,
  }))
);
const StandaloneChartGenerationView = lazy(() =>
  import("../pages/chartmind/ChartMindStandaloneViews").then((m) => ({
    default: m.StandaloneChartGenerationView,
  }))
);
const StandaloneV2PreEncounterView = lazy(() =>
  import("../pages/chartmind-v2/ChartMindV2StandaloneViews").then((m) => ({
    default: m.StandaloneV2PreEncounterView,
  }))
);
const StandaloneV2EncounterView = lazy(() =>
  import("../pages/chartmind-v2/ChartMindV2StandaloneViews").then((m) => ({
    default: m.StandaloneV2EncounterView,
  }))
);
const StandaloneV2ChartView = lazy(() =>
  import("../pages/chartmind-v2/ChartMindV2StandaloneViews").then((m) => ({
    default: m.StandaloneV2ChartView,
  }))
);

function RouteFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "40vh",
      }}
    >
      <CircularProgress aria-label="Loading page" />
    </Box>
  );
}

/** Redirect legacy /staff/accept/:id to canonical /join/d_:id */
function RedirectStaffAcceptToJoin() {
  const { designationId } = useParams();
  if (!designationId) return <Navigate to="/auth" replace />;
  return <Navigate to={`/join/d_${designationId}`} replace />;
}

/** Legacy /auth/accept-invitation?id=: render InvitationAcceptPage when id present, else redirect to /join/i_:id or auth */
function LegacyInvitationAcceptRoute() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  if (!id) return <Navigate to="/auth" replace />;
  return <InvitationAcceptPage invitationId={id} />;
}

/** Pathnames that must not be treated as map filter (URL/[word] → "viewing only [word] facilities"). Render the correct page so /ask-me-anything-2 works from direct links without login. */
const RESERVED_MAP_PATHS = [
  { path: '/ask-me-anything-2', element: <AskMeAnything2Page /> },
  { path: '/ask-me-anything', element: <AskMeAnythingPage /> },
];

function MapOrReservedPage() {
  const location = useLocation();
  const pathname = (location.pathname || '').replace(/\/$/, '') || '';
  const reserved = RESERVED_MAP_PATHS.find(({ path }) => pathname === path);
  if (reserved) return reserved.element;
  return <MapPage />;
}

function RequireAuthenticatedSymptomChecker({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user) {
    const normPath = (location.pathname || "").replace(/^\/+/, "/");
    const returnTo = `${normPath}${location.search || ""}${location.hash || ""}`;
    return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return children;
}

function RequireAuthenticatedApp() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user) {
    const normPath = (location.pathname || "").replace(/^\/+/, "/");
    const returnTo = `${normPath}${location.search || ""}${location.hash || ""}`;
    return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <Outlet />;
}

export const AppRoutes = () => (
  <Suspense fallback={<RouteFallback />}>
  <Routes>
    {/* Public auth entry points (keep public for existing dev auth flows) */}
    <Route path="/auth" element={<PhoneAuthPage />} />
    <Route path="/auth/qr-login" element={<QrLoginClaimPage />} />
    <Route path="/auth/accept-invitation" element={<LegacyInvitationAcceptRoute />} />
    <Route path="/login" element={<Navigate to="/auth" replace />} />
    <Route path="/join/:token" element={<UnifiedJoinPage />} />

    {/* Public SCMap (Symptom Checker + CareMap). Intentionally OUTSIDE RequireAuth
        — anonymous patients use it via signInAnonymously(). Do not wrap in any guard. */}
    <Route path="/scmap" element={<SCMapPage />} />
    <Route path="/scmap/help" element={<ScmapHelpPage />} />
    <Route path="/scmap2" element={<Scmap2Page />} />
    <Route path="/scmap2/help" element={<Scmap2HelpPage />} />

    {/* Legacy registration routes - redirect to new auth with role param */}
    <Route path="/register" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/register/patient" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/register/provider" element={<Navigate to="/auth?role=provider" replace />} />
    <Route path="/register/facility" element={<Navigate to="/auth?role=facility" replace />} />
    <Route path="/msi/register" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/pro/register" element={<Navigate to="/auth?role=provider" replace />} />
    <Route path="/facility/register" element={<Navigate to="/auth?role=facility" replace />} />

    {/* Other auth routes */}
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/wp-auth-handler" element={<WPAuthHandler />} />
    <Route path="/conversation/:dispatchId/:code" element={<StandaloneConversationPage />} />

    {/* Everything else in the app requires authentication */}
    <Route element={<RequireAuthenticatedApp />}>
      <Route
        path="/dashboard/*"
        element={
          <Elements stripe={stripePromise}>
            <LocationProvider>
              {/* Dashboard child routes (including /dashboard/conversation-templates*) are defined in PatientRoutes via DashboardPage. */}
              <DashboardPage />
            </LocationProvider>
          </Elements>
        }
      />

      {/* Patient Check-in routes are now authenticated */}
      <Route path="/checkin/:clinicId/:templateId/:code" element={<PatientCheckInPage />} />
      <Route path="/checkin/:clinicId/:templateId" element={<PatientCheckInPage />} />
      <Route path="/claim/verify-email" element={<EdClaimEmailVerifyPage />} />
      
      {/* Legacy: redirect to unified join (plan: single entry /join/:token) */}
      <Route path="/staff/accept/:designationId" element={<RedirectStaffAcceptToJoin />} />

      {/* Test Routes (dev/experimental) */}
      <Route path="/test/qr-scanner" element={<QRScannerTestPage />} />

      {/* Account Routes */}
      <Route path="/account" element={<Verify />} />
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Chat Routes */}
      <Route path="/kijabe-dashboard" element={<KijabeDashboard />} />
      <Route path="/kijabe" element={<KijabeChatPage />} />
      <Route path="/chat" element={<ChatPageWrapper />} />
      <Route path="/ask-me-anything" element={<AskMeAnythingPage />} />
      <Route path="/ask-me-anything-2" element={<AskMeAnything2Page />} />
      <Route path="/chatbot" element={<QueryChatPage />} />
      <Route path="/chatbot/:queryId" element={<QueryChatPage />} />

      {/* Virtual Queue Routes */}
      <Route path="/registration/*" element={<PatientRegistration />} />

      {/* Legal Routes */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/baa" element={<Baa />} />

      <Route path="/provider/cmr-share" element={<ProviderCmrSharePage />} />

      {/* LLM Manager: use dashboard layout (same left drawer) */}
      <Route path="/llm-manager" element={<Navigate to="/dashboard/llm-manager" replace />} />
      <Route path="/llm-manager/*" element={<Navigate to="/dashboard/llm-manager" replace />} />

      <Route path="/chartmind-admin" element={<ChartmindAdminDashboard />} />

      {/* Standalone ChartMind now authenticated */}
      <Route path="/chartmind" element={<ChartMindStandaloneLayout />}>
        <Route index element={<Navigate to="recording" replace />} />
        <Route path="recording" element={<StandaloneRecordingView />} />
        <Route path="chart-generation" element={<StandaloneChartGenerationView />} />
      </Route>
      <Route path="/chartmind-v2" element={<ChartMindV2StandaloneLayout />}>
        <Route index element={<Navigate to="new" replace />} />
        <Route path="new" element={<StandaloneV2PreEncounterView />} />
        <Route path="encounter" element={<StandaloneV2EncounterView />} />
        <Route path="chart" element={<StandaloneV2ChartView />} />
      </Route>

      {/* Standalone symptom checker routes require authenticated session */}
      <Route
        path="/symptomchecker"
        element={
          <RequireAuthenticatedSymptomChecker>
            <SymptomCheckerStandaloneLayout />
          </RequireAuthenticatedSymptomChecker>
        }
      />
      <Route
        path="/symptomcheckerv2"
        element={
          <RequireAuthenticatedSymptomChecker>
            <SymptomCheckV2StandaloneLayout />
          </RequireAuthenticatedSymptomChecker>
        }
      />
      <Route
        path="/symptomcheckerv3"
        element={
          <RequireAuthenticatedSymptomChecker>
            <SymptomCheckV3StandaloneLayout />
          </RequireAuthenticatedSymptomChecker>
        }
      />
      <Route
        path="/symptomcheckerv4"
        element={
          <RequireAuthenticatedSymptomChecker>
            <SymptomCheckV4StandaloneLayout />
          </RequireAuthenticatedSymptomChecker>
        }
      />

      {/* Standalone facility now authenticated */}
      <Route
        path="/getonthemap"
        element={
          <Elements stripe={stripePromise}>
            <LocationProvider>
              <FacilityStandaloneLayout />
            </LocationProvider>
          </Elements>
        }
      >
        <Route index element={<Navigate to="/getonthemap/clinic-health" replace />} />
        <Route path="clinic-health" element={<ClinicHealthPage />} />
        <Route path="clinic-queue" element={<UnifiedQueueDashboard />} />
        <Route path="settings" element={<Settings />} />
        <Route path="advertising" element={<StandaloneAdvertisingPage />} />
        <Route path="clinic-manager" element={<CheckInFormBuilder />} />
      </Route>

      {/* Root and all other pages now authenticated */}
      <Route path="/" element={<MapPage />} />
      <Route path="/map" element={<MapPage />} />
      <Route path="/msi3" element={<MSI3StandalonePage />} />
      <Route path="/splash" element={<MSI3SplashPage />} />
      <Route path="/splash/alt" element={<MSISplashAltRoute />} />
      <Route path="/splash/chartmind" element={<SplitFlapDisplayPage />} />
      <Route path="/splash/title-logos" element={<TitleLogosPage />} />
      <Route path="/splash/title-logos/msi3" element={<MSI3OnlyPage />} />
      <Route path="*" element={<MapOrReservedPage />} />
    </Route>
  </Routes>
  </Suspense>
);
